import { convertChannelCasing } from 'lib-shared-api-utils';
import { Channel } from 'lib-shared-types-channels';
import { useMemo } from 'react';

import { aiApi } from 'apis/ai';
import { findUnsupportedMimAttachments } from 'utils/mimDraft';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useDraftAiAgents } from 'utils/useDraftAiAgents';

/** The live facts an action's disabled state depends on. */
export type CopilotPreconditions = {
  hasStyleGuideForChannel: boolean;
  hasLanguage: boolean;
  hasDefaultDraftAgent: boolean;
  hasUnsupportedAttachment: boolean;
};

/**
 * Resolves every precondition a copilot action's status reads. Editors supply
 * their channel; the rest comes from the open conversation and user storage.
 */
export const useCopilotPreconditions = (
  channel?: Channel,
): CopilotPreconditions => {
  const { data: styleGuide } = aiApi.useReadAIWritingStyleGuideMatchQuery(
    {
      features: 'impr_reply',
      ...(channel && {
        channels: convertChannelCasing(channel, 'snake') as Channel,
      }),
    },
    // A style guide is per channel, so there is nothing to match without one.
    { skip: !channel, refetchOnMountOrArgChange: true },
  );

  const { defaultAgentId } = useDraftAiAgents({ skip: true });

  const messages = useAppSelector(
    (state) => state.conversation.metaConversation.messages,
  );
  const languageCode = useAppSelector(
    (state) =>
      state.conversation.metaConversation.conversation.attributes?.language,
  );

  const hasStyleGuideForChannel = !!styleGuide?.data?.match;
  const hasDefaultDraftAgent = !!defaultAgentId;

  return useMemo(
    () => ({
      hasStyleGuideForChannel,
      hasLanguage: !!languageCode,
      hasDefaultDraftAgent,
      hasUnsupportedAttachment:
        findUnsupportedMimAttachments(messages).length > 0,
    }),
    [hasStyleGuideForChannel, hasDefaultDraftAgent, languageCode, messages],
  );
};
